import React, { useState, useEffect } from 'react';
import {
  Box,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import ProjectAPIs from 'utilities/api/StudentProjectAPI';
import StudentGetUser from 'utilities/api/StudentAllUserAPIs';
import AddEditProjectDialog from './AddEditProjectDialog';
import ProjectCard from './ProjectCard';
import ProjectViewDrawer from './ProjectViewDrawer';
import nodatafoundImage from '../../utilities/dummy_assets/nodatafound.jpg'; // Import the No Data Found image
import { toast } from 'react-toastify';
import FilterIcon from '../icons/filterIcon';

const Projectscont = () => {
  const { user, access } = useSelector((state) => state.user);
  const [value, setValue] = useState(0);
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [drawerWidth, setDrawerWidth] = useState(400);
  const [isDragging, setIsDragging] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [isSearchBoxVisible, setIsSearchBoxVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [projects, setProjects] = useState([]);
  const [exploreProjects, setExploreProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [allUsers, setAllUsers] = useState([]); // State for storing all users
  const [showFilters, setShowFilters] = useState(false); // New state for showing filters on mobile

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const handleMouseEvents = (e) => {
      if (isDragging && !isMobile) {
        if (e.type === 'mousemove') setDrawerWidth(window.innerWidth - e.clientX);
        if (e.type === 'mouseup') setIsDragging(false);
      }
    };

    if (isDragging && !isMobile) {
      window.addEventListener('mousemove', handleMouseEvents);
      window.addEventListener('mouseup', handleMouseEvents);
    } else {
      window.removeEventListener('mousemove', handleMouseEvents);
      window.removeEventListener('mouseup', handleMouseEvents);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseEvents);
      window.removeEventListener('mouseup', handleMouseEvents);
    };
  }, [isDragging, isMobile]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await ProjectAPIs.ProjectsGet();
        setProjects(data);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      }
    };

    const fetchAllUsers = async () => {
      try {
        const users = await StudentGetUser.AllUserListGet(); // Fetch all users
        setAllUsers(users);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };

    fetchProjects();
    fetchAllUsers(); // Fetch all users when component mounts
  }, [user]);

  const toggleDrawer = (open, project = null) => {
    setDrawerOpen(open);
    setCurrentProject(open ? project : null);
  };

  const toggleViewDrawer = (open, project = null) => {
    setViewDrawerOpen(open);
    setCurrentProject(open ? project : null);
  };

  const handleTabChange = (event, newValue) => setValue(newValue);

  const handleSubmit = async (values) => {
    try {
      const updatedValues = { ...values, teammates: values.teammates };

      if (currentProject) {
        await ProjectAPIs.ProjectUpdate(currentProject.id, updatedValues);
        toast.success('Project updated successfully!');
      } else {
        await ProjectAPIs.ProjectCreate(access, updatedValues);
        toast.success('Project created successfully!');
      }

      toggleDrawer(false);
      const data = await ProjectAPIs.ProjectsGet();
      setProjects(data);
    } catch (error) {
      if (error.response?.status !== 400) {
        toast.error('Error submitting project. Please try again.');
      }
      console.error('Error submitting project:', error);
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!currentProject) return;

    try {
      await ProjectAPIs.ProjectDelete(currentProject.id);
      setProjects(projects.filter((project) => project.id !== currentProject.id));
      toggleDrawer(false);
      toast.success('Project deleted! Your project has been removed.');
    } catch (error) {
      toast.error('Oops! We couldn\'t delete the project. Please try again.');
      console.error('Failed to delete project:', error);
    }
  };

  const filterProjects = (projects) => {
    return projects.filter((project) => {
      const typeMatch = type ? project.type === type : true;
      const statusMatch = status ? project.progress_status === status : true;
      const searchMatch = searchQuery
        ? project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          project.description.toLowerCase().includes(searchQuery.toLowerCase())
        : true;
      return typeMatch && statusMatch && searchMatch;
    });
  };

  const filterMyProjects = (projects) => {
    return projects.filter(
      (project) => project.student === user.id || project.teammates.includes(user.id)
    );
  };

  const filterExploreProjects = (projects) => {
    return projects.filter((project) => project.is_project_visible);
  };

  const renderComponent = (project) => {
    return (
      <ProjectCard
        key={project.id}
        project={project}
        onEdit={(project) => toggleDrawer(true, project)}
        onView={(project) => toggleViewDrawer(true, project)}
        user={user}
        showVisibility={value === 1} // Show visibility icon for "Explore Projects"
        allUsers={allUsers} // Pass all users as a prop to ProjectCard
      />
    );
  };

  const tabSx = {
    flex: 1,
    textTransform: 'uppercase',
    fontWeight: 700,
    fontSize: '16px',
    minHeight: '48px',
    padding: '10px 28px',
    borderRadius: '12px',
    color: '#1F2A44',
    background: '#EEF1F8',
    border: 'none',
    boxShadow: 'inset 0 0 0 2px #6B7FB7', // Added inner border to each tab

    '&.Mui-selected': {
      background: '#273F77',
      color: 'white',
    },

    /*  MOBILE */
    '@media (max-width:600px)': {
      background: 'transparent',
      padding: '6px 12px',
      minHeight: 'auto',
      fontSize: '14px',
      fontWeight: 600,
      textTransform: 'none',
      boxShadow: 'none',

      '&.Mui-selected': {
        background: 'transparent',
        color: 'rgb(39,63,119)',
        border: '2px solid rgb(39,63,119)',
        borderRadius: '20px',
        padding: '6px 16px',
      },
    },
  };

  const Filters = ({ isMobile }) => (
    <>
      <FormControl
        variant='outlined'
        sx={{ minWidth: isMobile ? '100%' : 200, flex: isMobile ? 1 : 'none' }}
      >
        <InputLabel htmlFor='type-select'>Select Project Type</InputLabel>
        <Select
          value={type}
          onChange={(e) => setType(e.target.value)}
          label='Select Project Type'
          sx={{ borderRadius: '8px' }}
          inputProps={{ name: 'type', id: 'type-select' }}
        >
          <MenuItem value=''>
            <em>None</em>
          </MenuItem>
          <MenuItem value='1'>Academic</MenuItem>
          <MenuItem value='2'>Research</MenuItem>
          <MenuItem value='3'>Personal</MenuItem>
          <MenuItem value='4'>Other</MenuItem>
        </Select>
      </FormControl>
      <FormControl
        variant='outlined'
        sx={{ minWidth: isMobile ? '100%' : 200, flex: isMobile ? 1 : 'none' }}
      >
        <InputLabel htmlFor='status-select'>Select Status</InputLabel>
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          label='Select Status'
          sx={{ borderRadius: '8px' }}
          inputProps={{ name: 'status', id: 'status-select' }}
        >
          <MenuItem value=''>
            <em>None</em>
          </MenuItem>
          <MenuItem value='1'>Not Started</MenuItem>
          <MenuItem value='2'>In Progress</MenuItem>
          <MenuItem value='3'>Completed</MenuItem>
        </Select>
      </FormControl>
    </>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '2rem',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: isMobile ? 2 : 1,
        }}
      >
        <Tabs
          value={value}
          onChange={handleTabChange}
          indicatorColor='none'
          variant='standard'
          sx={{
            /* DESKTOP */
            padding: '6px',
            background: '#F1F4FB',
            borderRadius: '16px',
            width: { xs:'auto', md: 'fit-content'},
            boxSizing: 'border-box',
            border: '2px solid #6B7FB7',

            '& .MuiTabs-flexContainer': {
              gap: '12px',
            },

            /* MOBILE */
            '@media (max-width:600px)': {
              background: 'transparent',
              boxShadow: 'none',
              padding: 0,
              gap: '8px',
              border: 'none', // No border on mobile
            },
          }}
        >
          <Tab label='My Projects' sx={tabSx} />
          <Tab label='Explore Projects' sx={tabSx} />
        </Tabs>
        <Box
          sx={{
            display: 'flex',
            flexDirection: {xs: 'column', sm: 'row'},
            gap: 2,
            width: { xs: '100%', sm: 'auto'},
            alignItems: 'center',
            // marginTop: isMobile ? '1px' : '0',
          }}
        >
          {isMobile ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 1,
                  width: '100%',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    border: '1px solid #A6A6A6',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1,
                    overflow: 'hidden',
                  }}
                >
                  <TextField
                    autoFocus
                    sx={{ flexGrow: 1, ml: 1 }}
                    placeholder='Search...'
                    variant='standard'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{ disableUnderline: true }}
                  />
                </Box>

                <IconButton
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    border: '1px solid #A6A6A6',
                    color: '#7A7A7A',
                    backgroundColor: '#fff',
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: '#F5F5F5',
                    },
                  }}
                >
                  <FilterIcon />
                </IconButton>
              </Box>

              {showFilters && <Filters isMobile={isMobile} />}
            </>
          ) : (
            <>
              <Filters isMobile={isMobile} />
              <Box
                sx={{
                  border: '1px solid #E0E4EC',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  width: '240px',
                  backgroundColor: '#FFF',
                  padding: '4px 12px',
                  transition: 'border-color 0.2s',
                  '&:focus-within': {
                    border: '1px solid #6B7FB7'
                  }
                }}
              >
                <SearchIcon sx={{ color: '#9CA3AF', marginRight: '8px' }} fontSize="small" />
                  <TextField
                  placeholder="Search projects..."
                  variant="standard"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{ disableUnderline: true }}
                  sx={{ flexGrow: 1, '& input::placeholder': { fontSize: '0.9rem' } }}
                  />
              </Box>
            </>
          )}
          <Button
            variant='contained'
            color='warning'
            sx={{
              textTransform: 'none',
              backgroundColor: '#1F2A44',
              borderRadius: '8px',
              fontWeight: 600,
              padding: '6px 16px',
              width: isMobile ? '100%' : 'auto',
              boxShadow: '0 2px 6px rgba(31,42,68,0.15)',
              '&:hover': {
                backgroundColor: '#273F77',
                boxShadow: '0 4px 8px rgba(31,42,68,0.2)'
              }
            }}
            onClick={() => toggleDrawer(true)}
          >
            + New Project
          </Button>
        </Box>
      </Box>
      <Box sx={{ paddingTop: 2 }}>
        {projects.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)'},
              justifyItems: isMobile ? 'center' : 'start',
              gap: '30px',
            }}
          >
            {value === 0
              ? filterProjects(filterMyProjects(projects)).map((project) =>
                  renderComponent(project),
                )
              : filterProjects(projects).map((project) =>
                  renderComponent(project),
                )}
          </Box>
        ) : (
          <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            flexGrow={1}
            padding='40px'
          >
            <img
              src={nodatafoundImage}
              alt='No Data Found'
              style={{
                width: isMobile ? '80%' : '300px',
                marginBottom: '16px',
              }}
            />
            <Typography variant='h6' color='textSecondary'>
              No data available
            </Typography>
          </Box>
        )}
      </Box>
      <AddEditProjectDialog
        open={drawerOpen}
        onClose={() => toggleDrawer(false, null)}
        handleSubmit={handleSubmit}
        handleDelete={handleDelete}
        currentProject={currentProject}
        drawerWidth={drawerWidth}
        drawerOpen={drawerOpen}
        toggleDrawer={toggleDrawer}
        isMobile={isMobile} // Pass isMobile to adjust styles inside ProjectDrawer
      />
      <ProjectViewDrawer
        drawerWidth={drawerWidth}
        drawerOpen={viewDrawerOpen}
        toggleDrawer={toggleViewDrawer}
        currentProject={currentProject}
        allUsers={allUsers} // Pass allUsers to ProjectViewDrawer
        isMobile={isMobile} // Pass isMobile to adjust styles inside ProjectViewDrawer
      />
    </Box>
  );
};

export default Projectscont;
